/* eslint-disable next/no-img-element -- This is a Vite app; approved remote artwork is bounded by safeImage. */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions -- The labelled carousel observes hover/focus only to pause motion; activation belongs to native buttons. */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Pause, Pencil, Play } from 'lucide-react';
import type { HomeHeroPreferences, Meta } from '@streamit/contracts';
import {
  homeHeroPreferences,
  sameHomeHeroPreferences,
  validHomeHeroPreferences,
} from '../../../packages/contracts/src/home-hero';
import type { CatalogChoice } from '../../../packages/slc-web/src/catalogs';
import { homeHeroCatalogs, homeHeroCatalogName as catalogName, loadHomeHero } from '../../../packages/slc-web/src/home-hero';
import type { AddonContent } from '@/lib/addon-content';
import { api, json } from '@/lib/api';
import { safeImage } from '@/lib/catalog';
import { nativeMode } from '@/lib/native';
import { BrandMark } from '@/lib/brand';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useSession } from './session';
import './home-hero.css';

type HomeHeroProps = {
  client?: AddonContent;
  choices: CatalogChoice[];
  visible?: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
  onSelect: (meta: Meta, addonId?: string) => void;
};

/** A changed account, profile, preference or native refresh retires the whole
 * editor and its requests before any previous banner can paint in that context. */
export function HomeHero(props: HomeHeroProps) {
  const session = useSession();
  const profile = session.data?.profiles.find((p) => p.id === session.data?.profileId);
  const contextKey = JSON.stringify([
    session.data?.user.id, session.authorizationVersion, profile?.householdId,
    profile?.id, profile?.settings.homeHero,
    session.receiverInfoPending === true, session.receiverInfoEpoch || 0,
  ]);
  return <HomeHeroContent key={contextKey} {...props}/>;
}

/** Only the active profile's saved, explicitly selected feed can replace branding.
 * Unknown certifications never become eligible merely because a score is high. */
function HomeHeroContent({ client, choices, onSelect, visible = true, onRefresh, refreshing = false }: HomeHeroProps) {
  const session = useSession();
  const profile = session.data?.profiles.find(
    (p) => p.id === session.data?.profileId,
  );
  const infoPending = session.receiverInfoPending === true;
  const [preferences, setPreferences] = useState(() =>
    homeHeroPreferences(infoPending ? undefined : profile?.settings.homeHero),
  );
  const [draft, setDraft] = useState(preferences),
    [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false),
    [saveError, setSaveError] = useState('');
  const saveRequest = useRef<AbortController | undefined>(undefined);
  const [batch, setBatch] = useState<{
    key: string;
    items: Meta[];
    error?: string;
  }>();
  const [index, setIndex] = useState(0),
    [paused, setPaused] = useState(false);
  const [interacting, setInteracting] = useState(false),
    [focused, setFocused] = useState(false);
  const [pageVisible, setPageVisible] = useState(() => !document.hidden);
  const [reducedMotion, setReducedMotion] = useState(
    () => matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useLayoutEffect(() => () => saveRequest.current?.abort(), []);
  const catalogs = useMemo(() => homeHeroCatalogs(choices), [choices]);
  const choice =
    preferences.source === 'catalog'
      ? catalogs.find((c) => c.key === preferences.catalogKey)
      : undefined;
  const requestKey = JSON.stringify([profile?.id, preferences]);
  useEffect(() => {
    const abort = new AbortController();
    if (!client || !choice) return () => abort.abort();
    void loadHomeHero(choice, preferences, {
      catalog: async (feed, extra, signal) => (await client.catalog(feed, extra, signal)).items,
      metadata: (feed, meta, signal) => client.metadata(meta, signal, feed.addonId),
    }, abort.signal)
      .then(items => { if (!abort.signal.aborted) setBatch({ key: requestKey, items }); })
      .catch(() => {
        if (!abort.signal.aborted)
          setBatch({
            key: requestKey,
            items: [],
            error:
              'This catalog could not load. Your Streamit banner is shown.',
          });
      });
    return () => abort.abort();
  }, [client, choice, preferences, requestKey]);
  const items = choice && batch?.key === requestKey ? batch.items : [];
  const current = items[index % Math.max(1, items.length)];
  useEffect(() => {
    const visibility = () => setPageVisible(!document.hidden);
    const media = matchMedia('(prefers-reduced-motion: reduce)'),
      motion = () => setReducedMotion(media.matches);
    document.addEventListener('visibilitychange', visibility);
    media.addEventListener('change', motion);
    return () => {
      document.removeEventListener('visibilitychange', visibility);
      media.removeEventListener('change', motion);
    };
  }, []);
  const rotating =
    preferences.rotate &&
    !paused &&
    !interacting &&
    !focused &&
    !open &&
    visible &&
    pageVisible &&
    !reducedMotion &&
    items.length > 1;
  useEffect(() => {
    if (!rotating) return;
    const timer = setInterval(
      () => setIndex((i) => (i + 1) % items.length),
      9000,
    );
    return () => clearInterval(timer);
  }, [rotating, items.length, requestKey]);
  const move = (direction: number) => {
    setPaused(true);
    setIndex((i) => (i + direction + items.length) % items.length);
  };
  const canSave = !!profile && !infoPending;
  const save = async () => {
    if (!canSave || saving) return;
    const controller = new AbortController();
    saveRequest.current?.abort();
    saveRequest.current = controller;
    setSaving(true);
    setSaveError('');
    try {
      const result = await api<{ saved?: boolean; profileId?: string; homeHero?: unknown }>(nativeMode ? '/receiver/home-hero' : `/profiles/${profile.id}`, {
        method: 'PATCH',
        body: json(nativeMode ? { expectedProfileId: profile.id, homeHero: draft } : { settings: { homeHero: draft } }),
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      if (nativeMode && (result.saved !== true || result.profileId !== profile.id || !validHomeHeroPreferences(result.homeHero) || !sameHomeHeroPreferences(result.homeHero, draft)))
        throw new Error('The banner change could not be confirmed. Refresh your profile and try again.');
      setPreferences({ ...draft });
      setIndex(0);
      setPaused(false);
      setOpen(false);
      await session.refresh({ background: true });
    } catch (error) {
      if (!controller.signal.aborted) setSaveError((error as Error).message);
    } finally {
      if (!controller.signal.aborted) setSaving(false);
    }
  };
  const fallback = infoPending
    ? 'Refreshing your profile preferences…'
    : preferences.source === 'catalog' && !current
      ? !choice
        ? 'Your selected catalog is unavailable. Choose another with the pencil.'
        : batch?.key !== requestKey
          ? 'Finding posters for your preferences…'
          : batch.error ||
            'No posters match your filters. Titles with an unknown age rating stay hidden when an age limit is on.'
      : '';
  return (
    <section
      className={`hero home-feature ${current ? 'home-feature-media' : 'home-feature-brand'}${preferences.source === 'brand' ? ' home-feature-default' : ''}`}
      aria-label="Featured banner"
      onMouseEnter={() => setInteracting(true)}
      onMouseLeave={() => setInteracting(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null))
          setFocused(false);
      }}
    >
      {current && (
        <img
          className="home-feature-art"
          key={`${current.type}:${current.id}`}
          src={safeImage(current.background || current.poster)}
          alt=""
          referrerPolicy="no-referrer"
        />
      )}
      <div className="hero-shade" />
      <div className="hero-copy">
        {current ? (
          <>
            <p className="eyebrow">{choice && catalogName(choice)}</p>
            <h1>{current.name}</h1>
            <p className="metadata">
              {[
                current.releaseInfo,
                current.contentRating,
                current.imdbRating && `★ ${current.imdbRating}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
            <p className="description">{current.description}</p>
            <div className="hero-actions">
              <Button onClick={() => onSelect(current, choice?.addonId)}>
                Explore title <ChevronRight size={18} />
              </Button>
            </div>
          </>
        ) : (
          <>
            <h1 className="home-brand-lockup">
              <BrandMark size={76} />
              <span>
                streamit<span className="brand-dot">.</span>
              </span>
            </h1>
            <p className="home-brand-caption">Your library. Your screens.</p>
            {fallback && (
              <output className="home-feature-status">{fallback}</output>
            )}
          </>
        )}
      </div>
      <div className="home-feature-controls">
        {items.length > 1 && (
          <>
            <button
              type="button"
              aria-label="Previous featured title"
              onClick={() => move(-1)}
            >
              <ChevronLeft size={20} />
            </button>
            <span className="home-feature-count">
              {(index % items.length) + 1} / {items.length}
            </span>
            <button
              type="button"
              aria-label="Next featured title"
              onClick={() => move(1)}
            >
              <ChevronRight size={20} />
            </button>
            {preferences.rotate && !reducedMotion && (
              <button
                type="button"
                aria-label={
                  paused ? 'Resume banner rotation' : 'Pause banner rotation'
                }
                onClick={() => setPaused((p) => !p)}
              >
                {paused ? <Play size={17} /> : <Pause size={17} />}
              </button>
            )}
          </>
        )}
        <Dialog
          open={open}
          onOpenChange={(value) => {
            if (saving) return;
            if (value) {
              setDraft(preferences);
              setSaveError('');
            }
            setOpen(value);
          }}
        >
          <DialogTrigger
            disabled={infoPending}
            className="home-feature-edit"
            aria-label="Customize home banner"
            title="Customize home banner"
          >
            <Pencil size={18} />
          </DialogTrigger>
          <DialogContent className="home-feature-dialog">
            <DialogTitle>Make Home yours</DialogTitle>
            <DialogDescription>
              Choose the featured banner for {profile?.name || 'your profile'}.
              Your catalog rows stay where they are.
            </DialogDescription>
            {!canSave && (
              <p className="notice">
                {nativeMode ? 'Connect this screen to your profile to save a banner.' : 'Sign in to save a banner for your profile.'}
              </p>
            )}
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void save();
              }}
            >
              <fieldset disabled={!canSave || saving}>
                <label>
                  Banner source
                  <select
                    value={
                      draft.source === 'brand' ? '' : draft.catalogKey || ''
                    }
                    onChange={(event) =>
                      setDraft((d) => ({
                        ...d,
                        source: event.target.value ? 'catalog' : 'brand',
                        catalogKey: event.target.value || undefined,
                      }))
                    }
                  >
                    <option value="">Streamit logo and wordmark</option>
                    {draft.source === 'catalog' &&
                      !catalogs.some((c) => c.key === draft.catalogKey) && (
                        <option value={draft.catalogKey}>
                          Selected catalog unavailable
                        </option>
                      )}
                    {catalogs.map((c) => (
                      <option key={c.key} value={c.key}>
                        {catalogName(c)}
                      </option>
                    ))}
                  </select>
                </label>
                <p className="muted">
                  Popular movies or series are a good starting point. Netflix,
                  Prime and other collections appear here when a reviewed
                  installed catalog supplies them.
                </p>
                {draft.source === 'catalog' && (
                  <>
                    <label>
                      Maximum age rating
                      <select
                        value={draft.maxCertification}
                        onChange={(event) =>
                          setDraft((d) => ({
                            ...d,
                            maxCertification: event.target
                              .value as HomeHeroPreferences['maxCertification'],
                          }))
                        }
                      >
                        <option value="kids">
                          Kids · G / TV-Y / TV-Y7 / TV-G
                        </option>
                        <option value="family">
                          Family · up to PG / TV-PG
                        </option>
                        <option value="teen">
                          Teens · up to PG-13 / TV-14
                        </option>
                        <option value="all">
                          All age ratings, including unknown
                        </option>
                      </select>
                    </label>
                    <p className="muted">
                      Uses US movie and TV certifications. An age limit hides
                      unknown ratings too. Catalogs that omit them will show the
                      Streamit banner. This preference applies to the banner; it
                      does not restrict playback.
                    </p>
                    <label>
                      Minimum IMDb score
                      <select
                        value={draft.minScore}
                        onChange={(event) =>
                          setDraft((d) => ({
                            ...d,
                            minScore: Number(event.target.value),
                          }))
                        }
                      >
                        {[...new Set([0, 5, 6, 7, 8, 9, draft.minScore])]
                          .sort((a, b) => a - b)
                          .map((score) => (
                            <option value={score} key={score}>
                              {score ? `${score}+ out of 10` : 'Any score'}
                            </option>
                          ))}
                      </select>
                    </label>
                    <label className="home-feature-check">
                      <input
                        type="checkbox"
                        checked={draft.rotate}
                        onChange={(event) =>
                          setDraft((d) => ({
                            ...d,
                            rotate: event.target.checked,
                          }))
                        }
                      />
                      Automatically rotate posters
                    </label>
                    <p className="muted">
                      Changes every nine seconds. Pauses while you interact and
                      respects reduced motion.
                    </p>
                  </>
                )}
              </fieldset>
              {saveError && (
                <p role="alert" className="form-error">
                  {saveError}
                </p>
              )}
              <Button
                type="submit"
                disabled={
                  !canSave ||
                  saving ||
                  (draft.source === 'catalog' &&
                    !catalogs.some((c) => c.key === draft.catalogKey))
                }
              >
                {saving ? 'Saving…' : 'Save banner'}
              </Button>
            </form>
            {onRefresh && (
              <Button
                type="button"
                variant="ghost"
                disabled={refreshing || saving}
                onClick={onRefresh}
              >
                {refreshing
                  ? 'Refreshing catalogs…'
                  : 'Refresh available catalogs'}
              </Button>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}
